"use client";

import { useCallback, useMemo } from "react";
import { useLocalStorage } from "./use-local-storage";
import { STORAGE_KEYS, QUESTIONS } from "@/lib/constants";
import { LoveLanguage, Answers } from "@/types";

export function useQuiz() {
  const [answers, setAnswers, isHydrated] = useLocalStorage<Answers>(
    STORAGE_KEYS.ANSWERS,
    {}
  );

  const totalQuestions = QUESTIONS.length;
  const answeredCount = Object.keys(answers).length;
  const isComplete = answeredCount === totalQuestions;
  const hasProgress = answeredCount > 0;

  const currentQuestionIndex = useMemo(() => {
    for (let i = 0; i < QUESTIONS.length; i++) {
      if (!(QUESTIONS[i].id in answers)) {
        return i;
      }
    }
    return QUESTIONS.length - 1;
  }, [answers]);

  const setAnswer = useCallback(
    (questionId: number, answer: LoveLanguage) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: answer,
      }));
    },
    [setAnswers]
  );

  const resetQuiz = useCallback(() => {
    setAnswers({});
  }, [setAnswers]);

  const scores = useMemo(() => {
    const counts: Record<LoveLanguage, number> = {
      [LoveLanguage.A]: 0,
      [LoveLanguage.B]: 0,
      [LoveLanguage.C]: 0,
      [LoveLanguage.D]: 0,
      [LoveLanguage.E]: 0,
    };

    Object.values(answers).forEach((lang) => {
      counts[lang]++;
    });

    return counts;
  }, [answers]);

  const topLanguages = useMemo(() => {
    const maxScore = Math.max(...Object.values(scores));
    if (maxScore === 0) return [];

    return (Object.entries(scores) as [LoveLanguage, number][])
      .filter(([, score]) => score === maxScore)
      .map(([lang]) => lang);
  }, [scores]);

  const rankedLanguages = useMemo(() => {
    return (Object.entries(scores) as [LoveLanguage, number][])
      .sort((a, b) => b[1] - a[1])
      .map(([lang, score]) => ({ language: lang, score }));
  }, [scores]);

  return {
    answers,
    setAnswer,
    resetQuiz,
    totalQuestions,
    answeredCount,
    currentQuestionIndex,
    isComplete,
    hasProgress,
    isHydrated,
    scores,
    topLanguages,
    rankedLanguages,
  };
}
